/**
 * BOOT, AND THE ONE PLACE THAT DECIDES WHAT YOU ARE LOOKING AT.
 *
 * The old system reached 35,829 lines in one file partly because there was
 * never a moment where somebody decided "this is the router". Screens
 * showed and hid each other from wherever the click happened, so which
 * page you were on had no single answer and the back button had none at
 * all.
 *
 * Here the URL is the answer. A hash route, so no server configuration and
 * no deploy can ever 404 a deep link.
 */

import { loadConfig, configNow, db, primeShopDomain } from "./data/client.ts";
import { whoAmI, signOut, myDesks, deskUrl, type Who, type OtherDesk } from "./data/session.ts";
import { ppiBoard, ppiRequestPage, ppiNewPage, ppiMine, ppiReportPage, ppiInspectors } from "./pages/ppidesk.ts";
import { registerThisDevice } from "./data/push.ts";
import { primeWords, shopWords } from "./ui/words.ts";
import { primeZone } from "./ui/shopclock.ts";
import { cap } from "./domain/trade.ts";
import { mountFeed } from "./ui/feed.ts";
import { runbookPage } from "./pages/runbook.ts";
import { renderSignIn, renderNoProfile } from "./pages/sign-in.ts";
import { dashboardPage } from "./pages/dashboard.ts";
import { jobsPage, quotesPage } from "./pages/jobs.ts";
import { jobPage } from "./pages/job.ts";
import { techPage, techPathOn } from "./pages/tech.ts";
import { advisorPage } from "./pages/advisor.ts";
import { proPage, owedTier } from "./pages/pro.ts";
import { tracksFor } from "./domain/tracks.ts";
import { setWalker, setMayEnd } from "./pages/jobs.ts";
import { mayCancel } from "./domain/permissions.ts";
import { leadsPage, stopLeadsLive } from "./pages/leads.ts";
import { customersPage, customerPage } from "./pages/customers.ts";
import { schedulePage } from "./pages/schedule.ts";
import { invoicesPage } from "./pages/invoices.ts";
import { morePage } from "./pages/more.ts";
import { booksPage } from "./pages/books.ts";
import { accountingPage } from "./pages/accounting.ts";
import { walkthroughsPage, walkthroughPage } from "./pages/walkthroughs.ts";
import { forumPage, forumTopicPage, forumNewPage } from "./pages/forum.ts";
import { reportsPage } from "./pages/reports.ts";
import { callsPage } from "./pages/calls.ts";
import { callDeskPage } from "./pages/calldesk.ts";
import { isCallDesk } from "./domain/calldesk.ts";
import { workflowPage } from "./pages/workflow.ts";
import { photosPage } from "./pages/photos.ts";
import { inspectionPage } from "./pages/inspection.ts";
import { diagnosisPage } from "./pages/diagnosis.ts";
import { payPage } from "./pages/pay.ts";
import { clockPage, timesheetPage } from "./pages/clock.ts";
import { clockStatus } from "./data/pay.ts";
import { partsPage } from "./pages/parts.ts";
import { shopsPage } from "./pages/admin.ts";
import { automationsPage } from "./pages/automations.ts";
import { duplicatesPage } from "./pages/duplicates.ts";
import { certsPage } from "./pages/certs.ts";
import { contactsPage } from "./pages/contacts.ts";
import { mePage } from "./pages/me.ts";
import { terminalPage } from "./pages/terminal.ts";
import { billingPage } from "./pages/billing.ts";
import { teamPage, rememberMe } from "./pages/team.ts";
import { vehiclesPage, vehiclePage, settingsPage } from "./pages/shop.ts";
import { authorizationsPage } from "./pages/authorizations.ts";
import { agreementsPage } from "./pages/agreements.ts";
import { partConditionsPage } from "./pages/partconditions.ts";
import { commsPage } from "./pages/comms.ts";
import { deskPage } from "./pages/desk.ts";
import { queuePage, openQueueCount } from "./pages/queue.ts";
import { commandPage } from "./pages/command.ts";
import { SUPERCANIC_ACCOUNT } from "./domain/review-links.ts";
import { rentalsHome, rentalsBoard } from "./pages/rentals.ts";
import { tripPage, newTripPage } from "./pages/trip.ts";
import { rentalCalendarPage } from "./pages/rentcal.ts";
import { rentalMapPage } from "./pages/rentmap.ts";
import { hasRentals, type Side } from "./domain/side.ts";
import { currentSide, setSide } from "./ui/side.ts";
import { esc, mount, setMountTarget } from "./ui/dom.ts";
import { openShell, closeShell } from "./ui/shell.ts";
import { drawerMode, onDrawerModeChange } from "./ui/drawer.ts";
import { icon } from "./ui/icons.ts";
import { motionOn, motionLabel, setMotion, loadMotion } from "./ui/motion.ts";

/**
 * SIX, AND NO MORE.
 *
 * Anthony, on the system this replaces: "button overload and too many
 * options leading to a lack of path clarity." It had twenty-two top-level
 * pages, because things got added where there was room rather than where
 * they belonged.
 *
 * The rule now: a nav item is a PLACE YOU GO. Everything else is reached
 * from the thing it is about — invoices from the railway or from the
 * customer, parts from a job, a technician's pay from their own name. That
 * keeps every capability one click away without any of them competing for
 * attention with the work.
 */
/**
 * THREE, THE DESK, THREE.
 *
 * Desk sits in the middle rather than at the left end, because it is not
 * the first of a list — it is the place you come back to between the other
 * six. Centring it means the same thumb travel to get home from either
 * side of a phone, and on a wide screen it reads as a hub rather than as
 * item one of seven.
 *
 * The order around it follows the work: leads become quotes become jobs on
 * the left, and what supports them sits on the right.
 */
const LEFT = [
  { href: "#/leads", label: "Leads", icon: "leads" },
  { href: "#/quotes", label: "Quotes", icon: "quotes" },
  { href: "#/jobs", label: "Jobs", icon: "jobs" },
];
const HOME = { href: "#/", label: "Desk", icon: "desk" };
const RIGHT = [
  { href: "#/schedule", label: "Schedule", icon: "schedule" },
  { href: "#/customers", label: "Customers", icon: "customers" },
  { href: "#/more", label: "More", icon: "more" },
];
const NAV = [...LEFT, HOME, ...RIGHT];

/**
 * THE RENTALS SIDE'S SIX, for a shop that rents (src/domain/side.ts).
 *
 * Anthony, 2026-09-28: "rv rental on one side of their crm, rv repair on
 * the other, in one dash with a button to swap between the two". The six
 * rule above holds on this side too: six places you go in a day at an RV
 * yard, the same shape (three, the home, three) so the bar does not jump
 * when the switch flips.
 *
 *   Board, Calendar, Map on the left: the trips, by lane, by day and by
 *   place. The home in the middle is Today, who is coming to the yard.
 *   Fleet, Customers and More on the right: what supports them.
 *
 * Fleet is the vehicles screen under the yard's own word for it. Needs a
 * person is not on the bar on either side, for the reason MORE_LINKS
 * gives, and keeps its count at the top of the profile menu. New trip is
 * a button on the Board, Today and the Calendar rather than a seventh
 * place, because it is a thing you do and not somewhere you go.
 *
 * In Repair mode the bar is NAV exactly, so a car shop and an RV yard on
 * its repair side read precisely the bar they had before.
 */
const RENTAL_NAV = [
  { href: "#/rentals", label: "Board", icon: "board" },
  { href: "#/rentcal", label: "Calendar", icon: "schedule" },
  { href: "#/rentmap", label: "Map", icon: "pin" },
  { href: "#/", label: "Today", icon: "desk" },
  { href: "#/vehicles", label: "Fleet", icon: "vehicles" },
  { href: "#/customers", label: "Customers", icon: "customers" },
  { href: "#/more", label: "More", icon: "more" },
];

/**
 * THE PPI DESK'S BAR, for the PrePurchase Auto account (desk_mode 'ppi').
 * Anthony, 2026-10-14: PPI is not repair, so no Leads, Quotes or Jobs. The
 * board is the home, in the middle like every other desk's.
 */
const PPI_NAV = [
  { href: "#/ppinew", label: "New Request", icon: "leads" },
  { href: "#/ppi-mine", label: "My Inspections", icon: "jobs" },
  { href: "#/", label: "Board", icon: "board" },
  { href: "#/inspectors", label: "Inspectors", icon: "team" },
  { href: "#/team", label: "Team", icon: "team" },
  { href: "#/more", label: "More", icon: "more" },
];

/**
 * THE CALL DESK'S BAR, for the shops in src/domain/calldesk.ts. JEV for
 * NYIA, 2026-10-15 (D20): "stripped down to only: phone number, name,
 * phone recording, phone transcript, and SEND AGREEMENT button." One
 * place, because there is one screen. Team stays in the profile menu for
 * the owner, because somebody still has to add a login.
 */
/* PLUS THE CASE AND THE MONEY. Anthony, the same night: "Since they pay up
   front, that step needs moved right after the agreement is signed."
   Payment is taken on a case, so Calls alone was a dead end the moment a
   client signed. Quotes and Cases are where a case lives before and after
   the yes; Customers is where a number is looked up. Nothing else. */
const CALL_DESK_NAV = [
  { href: "#/", label: "Calls", icon: "comms" },
  /* Neelum, 2026-10-29: the callers marked Lead on their card. */
  { href: "#/calls/leads", label: "Leads", icon: "leads" },
  { href: "#/quotes", label: "Quotes", icon: "quotes" },
  { href: "#/jobs", label: "Cases", icon: "jobs" },
  { href: "#/customers", label: "Customers", icon: "customers" },
];
/** The screens a call desk may open besides Calls. */
/* "leads" too, though it is not on the bar: lead alerts and the Calls
   screen's form and text rows can still link a person to one. */
const CALL_DESK_ROUTES = new Set(["job", "jobs", "quotes", "customer", "customers", "leads"]);
const CALL_DESK_MENU = ["#/team"];

/** The bar for the side this browser is on. */
const navFor = (side: Side) => side === "rentals" ? RENTAL_NAV : NAV;

/**
 * What hangs off the profile on a phone. The same destinations the More
 * page lists, so there is one set of places and not two — a menu that
 * drifts from the page it mirrors is worse than not having it.
 */
const MORE_LINKS = [
  /* FIRST, FOR EVERY TRADE. Anthony, 2026-09-28: "a queue for problem
     people who want to talk to a human". Not on the bar, because the bar
     is six places you go and this is not a seventh: it is the list you
     glance at between them. Top of the menu, with a count, is where a
     glance lands. See src/pages/queue.ts. */
  { href: "#/queue", label: "Needs a person", icon: "comms", count: true },
  /* EVERY BUSINESS'S WHATSAPP AND TEXTS, for the house team only.
     Anthony, 2026-10-24. Another business's customers are in there, so it
     is not offered to a client shop at all. See src/pages/command.ts. */
  { href: "#/command", label: "Command", icon: "comms", houseOnly: true },
  /* THE TWO MANUALS, right under the queue. Rani Taki, 2026-10-15: "make
     it easier to get to the manual". They sat near the bottom, under
     Reports and Settings. How-to is the app's own manual (#/walkthroughs);
     Manuals is the repair side, behind the trade gate explained below. */
  { href: "#/walkthroughs", label: "How-to manual", icon: "manuals" },
  /* (see THE MANUAL SIDE note further down for why this row is gated) */
  { href: "/manuals", label: "Manuals", icon: "manuals", autoOnly: true },
  { href: "#/me", label: "My page", icon: "team" },
  /* THE TIME CLOCK, for hourly people only. Anthony, 2026-10-07: "make a
     clear log in log out". Drawn hidden and shown once clock_status() says
     this login is hourly, so nobody on commission is offered a clock that
     refuses them. See src/pages/clock.ts. */
  { href: "#/clock", label: "Time clock", icon: "payroll", hourlyOnly: true },
  /* (About the Manuals row, which moved up the menu on 2026-10-15.)
     THE MANUAL SIDE, FROM INSIDE THE DESK. Anthony: "can we get a manuals
     button in shop desk for everyone that takes them to the manual side?"
     It is the one part of this product that is free to every role and was
     only reachable by typing the address. An absolute href rather than a
     hash route because it is a different page, and the renderer below
     opens anything starting with "/" in a new tab so a technician does not
     lose the job they were reading.

     EVERY ROLE, BUT NOT EVERY TRADE. Anthony, 2026-09-19: "only auto
     repair trades get the button to the manuals side from the crm side."
     Behind that door is recalls, wiring diagrams, factory portals and a
     VIN decoder -- every source is a vehicle manufacturer, so a locksmith
     or a garage-door shop was being offered a shelf with nothing on it.

     Gated on hasPartsCatalog, which is the same flag the More page has
     always used for this row and is true for exactly the two auto trades.
     Reusing it rather than writing a second predicate: two gates for one
     question drift, and then the phone menu and the More page disagree
     about whether a shop has manuals. */
  { href: "#/forum", label: "Forum", icon: "comms" },
  { href: "#/reports", label: "Reports", icon: "desk", adminOnly: true },
  /* Every call, searchable, and what it turned into. See src/pages/calls.ts. */
  { href: "#/calls", label: "Calls", icon: "comms", adminOnly: true },
  { href: "#/more", label: "Everything else", icon: "more" },
  { href: "#/invoices", label: "Invoices", icon: "invoices" },
  { href: "#/authorizations", label: "Authorizations", icon: "authorizations" },
  { href: "#/comms", label: "Comms", icon: "comms" },
  { href: "#/vehicles", label: "Vehicles", icon: "vehicles" },
  { href: "#/team", label: "Team", icon: "team" },
  { href: "#/settings", label: "Settings", icon: "settings" },
  { href: "#/runbook", label: "Runbook", icon: "manuals" },
];

/**
 * The one nav label that is not the same word in every shop. The route
 * stays `#/vehicles` -- a URL is not copy, and changing it would break every
 * link anybody has bookmarked -- but what it is CALLED follows the shop's
 * trade, so a garage door shop reads "Doors". See src/domain/trade.ts.
 */
const navLabel = (n: { href: string; label: string }): string =>
  n.href === "#/vehicles" ? cap(shopWords().things) : n.label;

/**
 * The shop's logo for the top bar, or nothing. Only ever set on a shop's
 * own domain -- Supercanic's hosts have config.shop null and this returns
 * "", which is the bar exactly as it was.
 */
const shopLogo = (): string => {
  const s = configNow().shop;
  if (!s?.logoUrl) return "";
  /* BOTH are rendered and CSS shows one. The theme toggle does not
     redraw the bar, so swapping a src in JavaScript would leave the
     wrong logo up until the next navigation. */
  const light = s.logoUrlLight ?? s.logoUrl;
  return `<img class="brand-logo on-dark" src="${esc(s.logoUrl)}" alt="" onerror="this.remove()">`
    + `<img class="brand-logo on-light" src="${esc(light)}" alt="" onerror="this.remove()">`;
};

/** Two letters, so a face-less avatar still says who is signed in. */
const initialsOf = (n: string) =>
  n.trim().split(/\s+/).slice(0, 2).map((w) => w[0] ?? "").join("").toUpperCase() || "?";

/**
 * The host's default unless somebody said otherwise, and remembered when
 * they do. Stored per browser rather than on the profile: it is about the
 * room you are standing in, not about who you are.
 *
 * The default is dark everywhere except a rental yard's own domain, which
 * opens light (Anthony, 2026-09-28, src/domain/theme.ts). That is decided
 * in the page head before the bundle loads, so this only ever reads the
 * attribute it left. The toggle stores BOTH answers explicitly, "light"
 * and "dark", which is what lets a stored "dark" beat a light host: absence
 * is the only thing that means "the host's default".
 */
function currentTheme(): "dark" | "light" {
  return document.documentElement.getAttribute("data-theme") === "light" ? "light" : "dark";
}

function toggleTheme(): void {
  const next = currentTheme() === "light" ? "dark" : "light";
  if (next === "light") document.documentElement.setAttribute("data-theme", "light");
  else document.documentElement.removeAttribute("data-theme");
  try { localStorage.setItem("theme", next); } catch { /* private window */ }
  const b = document.getElementById("theme");
  if (b) b.textContent = next === "light" ? "☽" : "☀";
}

let motionFor = "";
let hourlyFor = "";
let hourlyNow: Promise<boolean> = Promise.resolve(false);

/**
 * WHETHER THIS LOGIN OWNS THE PLATFORM, asked once per person per page load.
 *
 * Anthony, 2026-10-15: "can't switch shops now that I logged into
 * shop.newyorkinvestigations.com cause the all shops switcher needs to be
 * there". The call desk's bar has four screens and no More page, so the
 * one way home from a switched-into call desk was gone. The owner gets All
 * shops back on that bar; the shop's own people never see it. The server
 * decides (is_platform_owner(), migration 0096), and a failed read is no.
 */
let ownerFor = "";
let ownerNow: Promise<boolean> = Promise.resolve(false);
function platformOwner(profileId: string): Promise<boolean> {
  if (ownerFor !== profileId) {
    ownerFor = profileId;
    ownerNow = db().then((sb) => sb.rpc("is_platform_owner"))
      .then(({ data, error }) => !error && data === true).catch(() => false);
  }
  return ownerNow;
}
const ALL_SHOPS_LINK = { href: "#/shops", label: "All shops", icon: "shop" };

function chrome(who: Extract<Who, { state: "in" }>): void {
  // So the team screen can refuse to let somebody demote themselves.
  rememberMe(who.profile.id);
  // The Animations switch follows the login. Once per person per page load.
  if (motionFor !== who.profile.id) { motionFor = who.profile.id; void loadMotion(); }
  const bar = document.getElementById("bar");
  if (!bar) return;
  const path = location.hash || "#/";
  const side = currentSide();
  /* An inspector on the PPI desk gets their own list and nothing that
     looks like the desk: the server refuses them the board and New
     Request anyway, so the bar must not offer them (live check 2026-10-14). */
  const callDesk = isCallDesk(who.account.id);
  const nav = callDesk ? [...CALL_DESK_NAV, ALL_SHOPS_LINK]
    : who.account.desk_mode !== "ppi" ? navFor(side)
    : who.profile.role === "technician" ? PPI_NAV.filter((n) => n.href === "#/ppi-mine" || n.href === "#/more")
    : PPI_NAV;
  const twoSides = !callDesk && hasRentals(who.account.trade);
  bar.innerHTML = `
    <!-- THE SHOP'S OWN MARK, TOP LEFT. Anthony, 2026-09-16: "give the
         inside their own brand and logo look." Their logo when they have
         one (config.shop.logoUrl, set on their own domain), their name
         otherwise -- which is what every shop had before. An image that
         fails to load removes itself, so a broken URL falls back to the
         name rather than leaving a gap where the brand should be. -->
    <div class="brand">${shopLogo()}<span class="brand-name">${
      esc(who.account.name.replace(/,?\s*(llc|inc|ltd|co)\.?$/i, "").trim() || "Shop Desk")}</span>
      ${configNow().stage !== "production" ? `<span class="stage">${esc(configNow().stage)}</span>` : ""}
    </div>
    <!-- RENTALS OR REPAIR, for a shop that has both and nobody else. Two
         segments, one pressed, arrow keys move between them. See
         src/domain/side.ts for who gets it and why Rentals is the default. -->
    ${twoSides ? `<div class="sideswitch" role="radiogroup" aria-label="Rentals or repair">${
      (["rentals", "repair"] as const).map((s) => `<button type="button" role="radio" data-side="${s}"
        aria-checked="${side === s}" tabindex="${side === s ? 0 : -1}" class="${side === s ? "on" : ""}">${
        s === "rentals" ? "Rentals" : "Repair"}</button>`).join("")}</div>` : ""}
    <nav>${nav.map((n) =>
      `<a href="${n.href}" class="${path === n.href ? "on" : ""}${n.href === "#/" ? " home" : ""}"${
        n === ALL_SHOPS_LINK ? " data-owner hidden" : ""}>${
        icon(n.icon)}<span>${esc(n.label)}</span></a>`).join("")}</nav>
    <div class="me">
      <!-- WHO IS WAITING ON A PERSON, in the top right where somebody
           already looks for it. See src/ui/feed.ts. -->
      ${callDesk ? "" : `<div id="feed" class="feedwrap"></div>`}
      <span>${esc(who.profile.full_name)}<i>${esc(who.profile.role)}</i></span>
      <!-- THE HOW-TO MANUAL, one tap from every screen. Rani Taki (Manko
           Mobile), 2026-10-15: "make it easier to get to the manual ... a
           manual for navigating the CRM and shop desk". It was the second
           to last row of the profile menu. Shown on a phone too, where the
           theme switch beside it is not. -->
      ${callDesk ? "" : `<a href="#/walkthroughs" class="themebtn helpbtn" title="How-to manual" aria-label="How-to manual">?</a>`}
      <button id="theme" class="themebtn" title="Light or dark">${
        currentTheme() === "light" ? "☽" : "☀"}</button>
      <!-- YOUR OTHER DESKS. Eber dispatches on Supercanic, the PPI desk and
           MechanicRank, three logins on three hosts. Anthony, 2026-10-15:
           switch "without logging out or have more than one window open
           with each". Filled by mountDesks(); hidden for a one-desk login. -->
      <button id="deskbtn" class="btn ghost sm" hidden aria-haspopup="true" aria-expanded="false">Desks &#9662;</button>
      <div id="deskmenu" class="profmenu deskmenu" hidden></div>
      <button id="signout" class="btn ghost sm">Sign out</button>

      <!-- On a phone the bottom bar is six places you GO. Everything else
           hangs off who you are, which is where a person already looks for
           it, and it costs the bottom bar nothing. -->
      <button id="avatar" class="avatarbtn" aria-haspopup="true" aria-expanded="false"
              title="${esc(who.profile.full_name)}">${esc(initialsOf(who.profile.full_name))}</button>
      <div id="profmenu" class="profmenu" hidden>
        <div class="profmenu-who">${esc(who.profile.full_name)}
          <i>${esc(who.profile.role)} &middot; ${esc(who.account.name)}</i></div>
        <div id="desklist"></div>
        ${MORE_LINKS
          .filter((m) => !callDesk || CALL_DESK_MENU.includes(m.href))
          .filter((m) => !callDesk || who.profile.role === "admin" || who.profile.role === "supervisor")
          .filter((m) => !m.adminOnly || who.profile.role === "admin" || who.profile.role === "supervisor")
          .filter((m) => !m.houseOnly || (who.account.id === SUPERCANIC_ACCOUNT
            && ["admin", "supervisor", "advisor"].includes(who.profile.role)))
          .filter((m) => !m.autoOnly || shopWords().hasPartsCatalog)
          .map((m) => `<a href="${m.href}"${m.href.startsWith("/") ? ' target="_blank" rel="noopener"' : ""}${
            m.hourlyOnly ? ' id="clock-link" hidden' : ""}>${icon(m.icon)}<span>${esc(navLabel(m))}</span>${
            m.count ? `<i class="navcount" id="hq-count" hidden></i>` : ""}</a>`).join("")}
        <div class="profmenu-sep"></div>
        <button id="theme2">${currentTheme() === "light" ? "Dark mode" : "Light mode"}</button>
        <button id="motion2">${esc(motionLabel())}</button>
        <button id="signout2">Sign out</button>
      </div>
    </div>`;
  bar.hidden = false;
  mountFeed("feed");
  void mountDesks();
  /* The count beside Needs a person, filled in when it arrives. Not
     awaited: the bar is drawn on every navigation and must never wait on a
     count. Hidden at zero, and at zero when the read fails. */
  /* The Time clock row, shown once we know this login is hourly. Asked
     once per person per page load, not on every navigation. */
  if (hourlyFor !== who.profile.id) {
    hourlyFor = who.profile.id;
    hourlyNow = clockStatus().then((c) => c.hourly).catch(() => false);
  }
  void hourlyNow.then((yes) => {
    const a = document.getElementById("clock-link");
    if (a) a.hidden = !yes;
  });
  /* All shops on a call desk's bar, once the server says this is the owner. */
  if (callDesk) void platformOwner(who.profile.id).then((yes) => {
    document.querySelectorAll<HTMLElement>("[data-owner]").forEach((a) => { a.hidden = !yes; });
  });
  void openQueueCount().then((n) => {
    const b = document.getElementById("hq-count");
    if (b) { b.textContent = String(n); b.hidden = n === 0; }
  });

  /**
   * THE SAME SIX, AT THE BOTTOM, ON A PHONE.
   *
   * Anthony: "consider putting buttons in bottom of screen instead of just
   * the top." A thumb reaches the bottom of a phone and does not reach the
   * top of one, and this is a tool used one-handed next to a car. The top
   * bar keeps the shop name and the sign-out; the destinations move to
   * where the hand already is.
   *
   * Same links, same order, one source — a second nav that could drift
   * from the first would be worse than no second nav.
   */
  let tabs = document.getElementById("tabbar");
  if (!tabs) {
    tabs = document.createElement("nav");
    tabs.id = "tabbar";
    document.body.appendChild(tabs);
  }
  tabs.innerHTML = nav.filter((n) => n.href !== "#/more").map((n) =>
    `<a href="${n.href}" class="${path === n.href ? "on" : ""}${n.href === "#/" ? " home" : ""}"${
        n === ALL_SHOPS_LINK ? " data-owner hidden" : ""}>${
      icon(n.icon)}<span>${esc(n.label)}</span></a>`).join("");

  /**
   * HOW TALL THE BOTTOM BAR IS, MEASURED.
   *
   * Anthony: "I can't reach sign out still from fold out menu on mobile,
   * its been a bug for some time."
   *
   * The fold-out menu is a scrolling box whose height was the viewport
   * less the header. The bar is fixed at the bottom of that same viewport,
   * so the last item in the menu -- Sign out, because it is last -- sat
   * underneath it. Scrolling could not help: the scroll container's own
   * bottom edge was behind the bar.
   *
   * Written as a variable rather than a number because the bar's height is
   * a font, an icon size, three paddings and a safe-area inset on a
   * notched phone. Every one of those has moved before. Asking the element
   * cannot be wrong.
   */
  const setBarHeight = () => {
    const h = tabs && getComputedStyle(tabs).display !== "none" ? tabs.offsetHeight : 0;
    document.documentElement.style.setProperty("--tabbar-h", `${h}px`);
  };
  setBarHeight();
  /* A phone turned on its side stops showing the bar at all. */
  window.removeEventListener("resize", setBarHeight);
  window.addEventListener("resize", setBarHeight);
  document.getElementById("theme")?.addEventListener("click", toggleTheme);

  /* THE SWITCH. Remembered for this browser, then home on the side you
     chose, because the screen you were on belongs to the other side. The
     same hash does not fire hashchange, so going home from home routes by
     hand. Arrow keys move between the two, as a radio group's do. */
  const pick = (s: Side) => {
    if (s === currentSide()) return;
    setSide(s);
    if ((location.hash || "#/") === "#/") void route(who);
    else location.hash = "#/";
  };
  bar.querySelectorAll<HTMLButtonElement>(".sideswitch [data-side]").forEach((b) => {
    b.addEventListener("click", () => pick(b.dataset["side"] as Side));
    b.addEventListener("keydown", (e) => {
      if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) return;
      e.preventDefault();
      const other: Side = b.dataset["side"] === "rentals" ? "repair" : "rentals";
      pick(other);
      document.querySelector<HTMLButtonElement>(`.sideswitch [data-side="${other}"]`)?.focus();
    });
  });
  document.getElementById("theme2")?.addEventListener("click", toggleTheme);
  document.getElementById("motion2")?.addEventListener("click", (e) => {
    setMotion(!motionOn());
    (e.currentTarget as HTMLButtonElement).textContent = motionLabel();
  });

  const menu = document.getElementById("profmenu");
  const avatar = document.getElementById("avatar");
  const shut = () => { if (menu) menu.hidden = true; avatar?.setAttribute("aria-expanded", "false"); };
  avatar?.addEventListener("click", (e) => {
    e.stopPropagation();
    if (!menu) return;
    menu.hidden = !menu.hidden;
    avatar.setAttribute("aria-expanded", String(!menu.hidden));
  });
  // Anywhere else, Escape, or following a link all close it. A menu that
  // survives a tap on the page behind it is a menu in the way.
  document.addEventListener("click", shut);
  document.addEventListener("keydown", (e) => { if (e.key === "Escape") shut(); });
  menu?.querySelectorAll("a").forEach((a) => a.addEventListener("click", shut));
  document.getElementById("signout2")?.addEventListener("click", async () => {
    await signOut(); location.hash = "#/"; void start();
  });
  document.getElementById("signout")?.addEventListener("click", async () => {
    await signOut();
    location.hash = "#/";
    void start();
  });
}

/**
 * THE DESK SWITCHER. Each row opens that desk in this tab; the square
 * beside it opens it in a new window, so two desks can sit side by side.
 * Every desk keeps its own sign-in on its own host, so nothing here signs
 * anybody out. The new window is opened inside the click, before the
 * server is asked for the link, or the browser calls it a pop-up and
 * blocks it. Asked once per page load.
 */
let desksNow: Promise<OtherDesk[]> | null = null;
async function mountDesks(): Promise<void> {
  desksNow ??= myDesks();
  const all = await desksNow;
  const others = all.filter((d) => !d.current);
  if (!others.length) return;
  const here = all.find((d) => d.current);
  const rows = `<div class="profmenu-who">Switch desk<i>${esc(here?.name ?? "")} is open here</i></div>` +
    others.map((d, i) => `<div class="deskrow"><a href="https://${esc(d.host)}/index.html" data-desk="${i}">${
      esc(d.name)}<i>${esc(d.host)}</i></a><button type="button" data-desk-new="${i}" title="Open ${
      esc(d.name)} in a new window" aria-label="Open ${esc(d.name)} in a new window">&#10697;</button></div>`).join("");
  const menu = document.getElementById("deskmenu");
  const list = document.getElementById("desklist");
  const btn = document.getElementById("deskbtn");
  if (menu) menu.innerHTML = rows;
  if (list) list.innerHTML = rows + `<div class="profmenu-sep"></div>`;
  if (btn && menu) {
    btn.hidden = false;
    btn.onclick = (e) => {
      e.stopPropagation();
      menu.hidden = !menu.hidden;
      btn.setAttribute("aria-expanded", String(!menu.hidden));
    };
    document.addEventListener("click", () => { menu.hidden = true; btn.setAttribute("aria-expanded", "false"); });
  }
  for (const root of [menu, list]) {
    root?.querySelectorAll<HTMLAnchorElement>("a[data-desk]").forEach((a) => a.addEventListener("click", (e) => {
      const d = others[Number(a.dataset["desk"])];
      if (!d) return;
      e.preventDefault();
      const fresh = e.metaKey || e.ctrlKey || e.shiftKey;
      const win = fresh ? window.open("about:blank", "_blank") : null;
      void deskUrl(d).then((url) => { if (win) win.location.href = url; else location.href = url; });
    }));
    root?.querySelectorAll<HTMLButtonElement>("button[data-desk-new]").forEach((b) => b.addEventListener("click", (e) => {
      e.stopPropagation();
      const d = others[Number(b.dataset["deskNew"])];
      if (!d) return;
      const win = window.open("about:blank", "_blank");
      void deskUrl(d).then((url) => { if (win) win.location.href = url; else location.href = url; });
    }));
  }
}

/** #/job/<id> and friends. Kept deliberately dumb. */
/**
 * WHAT THE PANEL CALLS ITSELF.
 *
 * The nav already names six of these and the More menu names eleven more,
 * so the titles come from those lists rather than from a third one that
 * would drift. What is left are the screens you reach from the thing they
 * are about — a job from a list, a technician from their own name — and
 * those are spelled out here.
 */
const SHELL_TITLES: Record<string, string> = {
  job: "Job", customer: "Customer", tech: "The walk", advisor: "The walk",
  pro: "Wrench Pro", quotes: "Quotes", jobs: "Jobs", leads: "Leads",
  schedule: "Schedule", customers: "Customers", invoices: "Invoices",
  vehicles: "Vehicles", team: "Team", settings: "Settings",
  authorizations: "Authorizations", comms: "Comms", reports: "Reports",
  walkthrough: "Walkthrough", walkthroughs: "Walkthroughs", inspection: "Inspection", "ppi-job": "PPI report",
  diagnosis: "Diagnostic card",
  photos: "Photos", parts: "Parts", certs: "Certifications", contacts: "Contacts",
  me: "My page", terminal: "Terminal", billing: "Billing", duplicates: "Duplicates",
  shops: "Shops", automations: "Automations", desk: "Text desk", runbook: "Runbook",
  forum: "Forum", queue: "Needs a person", command: "Command",
  rentals: "The board", trip: "Trip", newtrip: "New trip", rentcal: "Fleet calendar", rentmap: "Where the RVs are",
  workflow: "Workflow", more: "Everything else", pay: "Pay", payroll: "Payroll",
  hours: "Hours", payouts: "Payouts", mypay: "My pay",
  clock: "Time clock", timesheet: "Timesheets", books: "Books", accounting: "Accounting",
};

/**
 * Two of these are the shop's own word rather than a fixed one, and the
 * map is a module constant so they cannot live in it -- it is built before
 * primeWords() has run. Answered here, at call time, where the rest of the
 * per-trade labels are answered.
 */
const shellTitle = (head: string): string =>
  (head === "vehicles" ? cap(shopWords().things)
    : head === "vehicle" ? cap(shopWords().thing)
    : head === "parts" ? cap(shopWords().partsLabel)
      : undefined)
  ?? SHELL_TITLES[head]
  ?? NAV.find((n) => n.href === `#/${head}`)?.label
  ?? MORE_LINKS.find((n) => n.href === `#/${head}`)?.label
  ?? "Shop Desk";

/**
 * IS THE MIDDLE STILL THERE.
 *
 * Landing straight on a deep link — a job from a text message, a bookmark
 * on the shop tablet — runs the route without the day screen ever having
 * been drawn, so the panel would open over the loading card the page ships
 * with. This paints the day underneath on the way past, once.
 */
function middleMissing(): boolean {
  const app = document.getElementById("app");
  return !app || !app.querySelector(".page");
}

/**
 * Which mode the last route ran in, so the next one can tell whether the
 * window crossed the line since. `null` until the first route, because the
 * first one has not flipped from anything.
 */
let lastDrawer: boolean | null = null;

/** Whether this session has asked GET /api/pro/owed yet. See route(). */
let askedOwed = false;
/** The plan this shop still owes its first payment on, from that answer. */
let owed: "solo" | "pro" | null = null;
/** The screen the last route drew, so leaving Go Pro asks again. */
let lastHead = "";

/**
 * THE STRIP OVER EVERY SCREEN WHILE A PLAN IS OWED.
 *
 * Anthony, 2026-10-10: the once-a-session redirect to Go Pro was the only
 * thing that said "pay here", and a shop that tapped away from it never
 * saw it again. Since the same day a Solo or Pro signup opens on free, so
 * this is also the only thing on screen saying what they asked for and
 * how to switch it on. Hidden on Go Pro itself, and gone once the card
 * goes through, because leaving Go Pro asks the server again.
 */
function owedStrip(tier: "solo" | "pro" | null): void {
  let el = document.getElementById("owedbar");
  if (!tier) { el?.remove(); return; }
  if (!el) {
    el = document.createElement("div");
    el.id = "owedbar";
    el.setAttribute("role", "status");
    document.getElementById("app")?.before(el);
  }
  const word = tier === "solo" ? "Solo" : "Pro";
  el.innerHTML = `<span>You asked for ${word}. It switches on when your card goes through.</span>
    <a class="btn primary sm" href="#/pro">Start ${word}</a>`;
}

async function route(who: Extract<Who, { state: "in" }>): Promise<void> {
  chrome(who);
  /* THE LEADS SCREEN POLLS ITSELF, so leaving it has to switch that off.
     Its timer re-runs leadsPage, which mounts -- so left running it would
     paint the leads list over whatever you navigated to, twenty seconds
     after you got there. leadsPage starts a fresh one on its way in. */
  stopLeadsLive();
  const hash = (location.hash || "#/").slice(1);
  const [, head, arg] = hash.split("/");
  /* A SHOP THAT SIGNED UP TO PAY OPENS ON THE CHECKOUT, once a session.
     Anthony, 2026-10-10: Loyd Pearson was approved for Solo, signed in, and
     could not find where to put his card -- the app opened on the day
     screen and nothing said "pay here". Only the home is redirected, so a
     deep link from a text still opens what it links to. */
  if (!askedOwed || (lastHead === "pro" && head !== "pro")) {
    const first = !askedOwed;
    askedOwed = true;
    owed = await owedTier();
    if (first && !head && owed) { lastHead = ""; location.hash = "#/pro"; return; }
  }
  lastHead = head ?? "";
  owedStrip(head === "pro" ? null : owed);
  // The jobs list draws a walk link per row and does not take a role.
  setWalker(techPathOn(who.profile.role));
  /* Archive sets `cancelled`, so it is a cancel however it is labelled.
     setStatus refuses it for a technician whatever screen asks; this stops
     the list offering a button that would throw. */
  setMayEnd(mayCancel(who.profile.role).allowed);

  /* THE PPI DESK takes the home and its own four screens, and leaves the
     shop-wide ones (team, settings, more) as they are. The repair screens
     are not served here: a PPI that wandered into Quotes is the mess this
     desk exists to end. See src/pages/ppidesk.ts. */
  if (who.account.desk_mode === "ppi") {
    if (who.profile.role === "technician" && (!head || head === "ppinew")) return void ppiMine();
    if (!head || ["leads", "quotes", "jobs", "job", "invoices", "schedule"].includes(head)) return void ppiBoard();
    if (head === "ppi" && arg) return void ppiRequestPage(arg);
    if (head === "ppinew") return void ppiNewPage();
    if (head === "ppi-mine") return void ppiMine();
    if (head === "ppi-report" && arg) return void ppiReportPage(arg);
    if (head === "inspectors" && who.profile.role !== "technician") return void ppiInspectors();
  }

  /* THE CALL DESK has one screen, full width, and every other address
     lands on it. JEV for NYIA, 2026-10-15 (D20): "start fresh with just
     this minimal list". Team for the owner so logins can still be added,
     and Go Pro because a shop that owes a plan has to be able to pay it.
     See src/domain/calldesk.ts. */
  if (isCallDesk(who.account.id)) {
    closeShell();
    setMountTarget("app");
    if (head === "pro") return void proPage(who.profile.role);
    if (head === "team" && (who.profile.role === "admin" || who.profile.role === "supervisor")) {
      return void teamPage(who.profile.role);
    }
    if (!head) return void callDeskPage();
    if (head === "calls" && arg === "leads") return void callDeskPage(true);
    if (head === "shops") {
      return void platformOwner(who.profile.id).then((yes) =>
        yes ? shopsPage(who.profile.role) : void (location.hash = "#/"));
    }
    if (!CALL_DESK_ROUTES.has(head)) { location.hash = "#/"; return; }
    /* falls through to the ordinary routing below */
  }

  /**
   * THE MIDDLE STAYS, THE SCREENS MOVE TO THE EDGE — ON A DESKTOP.
   *
   * This is the staging side-panel system, back on live and this time
   * fenced in. It was here once before and Anthony had it pulled out on
   * 2026-09-14 (bdef49d) because it applied EVERYWHERE: a screen in a
   * 31rem column on a phone is a worse page, not a better one, and the
   * shop tablet got it worst of all.
   *
   * So the panel now has a door it only fits through. On a full-screen
   * desktop the screens live at the edge and the day stays in the middle,
   * which is what it was for. Anywhere else the screen takes `#app` full
   * width and navigating is navigating, exactly as it does today. See
   * `drawerMode` in ui/drawer.ts for where the line is and why.
   *
   * The panel is opened BEFORE the screen runs, because a screen paints
   * the moment it has data and a target that does not exist yet is a
   * screen dropped on the floor.
   *
   * THE FLIP IS THE HARD CASE. `middleMissing` asks whether the middle
   * holds a `.page`, and off a desktop the SCREEN is that `.page` — so
   * coming back up into drawer mode it would answer "the day is already
   * there" about a job card and open the panel over the wrong thing.
   * Crossing the line is its own reason to repaint the day, separate from
   * arriving on a deep link with nothing behind it.
   */
  const drawer = drawerMode();
  const flipped = lastDrawer !== null && lastDrawer !== drawer;
  lastDrawer = drawer;

  if (!drawer) {
    closeShell();
    setMountTarget("app");
  } else if (!head) {
    closeShell();
    setMountTarget("app");
    return void home(who);
  } else {
    if (flipped || middleMissing()) void home(who);
    openShell(shellTitle(head));
    setMountTarget("shell-body");
  }

  if (head === "job" && arg) return void jobPage(arg, who.profile.role);
  /* THE TECHNICIAN'S PATH. Live, and gated on the role rather than the
     deployment: a service advisor typing the URL gets nothing, because
     the funnel would take their screen away and call it help. */
  if (head === "tech" && arg && techPathOn(who.profile.role)) {
    return void techPage(arg, who.profile.role);
  }
  /* THE SERVICE ADVISOR'S PATH. Same gate, other half of the job: who they
     are, a price, the send, the yes, the booking, the bill, the money. A
     technician typing the URL lands nowhere, for the same reason an
     advisor cannot open the tech walk -- a guided path you cannot finish
     is worse than no path at all. */
  if (head === "advisor" && arg && tracksFor(who.profile.role).includes("advisor")) {
    return void advisorPage(arg, who.profile.role);
  }
  /* The checkout lives in the app because the app holds a live session.
     A static page reading the JWT out of localStorage gets whatever was
     last written there, which after an hour is a dead token -- and a dead
     token reads to a shop owner like a broken checkout. */
  if (head === "pro") return void proPage(who.profile.role);
  if (head === "customer" && arg) return void customerPage(arg, who.profile.role);
  if (head === "jobs" && arg) return void jobsPage(arg, hash.split("/")[3]);
  if (head === "jobs") return void jobsPage();
  if (head === "runbook") return void runbookPage();
  if (head === "quotes") return void quotesPage();
  if (head === "leads") return void leadsPage(arg, who.profile.role);
  if (head === "schedule") return void schedulePage();
  if (head === "customers") return void customersPage();
  if (head === "invoices") return void invoicesPage();
  if (head === "vehicles") return void vehiclesPage();
  /* ONE OF THEM, not the list. The job card has linked here since the
     vehicle tab was written and nothing served it, so "Everything done to
     this car" quietly went to the dashboard. See vehiclePage in shop.ts. */
  if (head === "vehicle" && arg) return void vehiclePage(arg, who.profile.role);
  if (head === "team") return void teamPage(who.profile.role);
  if (head === "settings") return void settingsPage(who.profile.role);
  if (head === "authorizations") return void authorizationsPage();
  if (head === "agreements") return void agreementsPage();
  if (head === "partconditions") return void partConditionsPage();
  if (head === "comms") return void commsPage(arg);
  if (head === "reports") return void reportsPage(who.profile.role);
  if (head === "calls") return void callsPage(who.profile.role);
  if (head === "walkthrough" && arg) return void walkthroughPage(arg, who.profile.role);
  if (head === "inspection" && arg) return void inspectionPage(arg, who.profile.role);
  // The PrePurchaseAuto report on the shop's own PPI job (2026-10-29).
  if (head === "ppi-job" && arg) return void ppiReportPage(arg, true);
  // Phase 10, the diagnostic card: #/diagnosis/<job id>. See pages/diagnosis.ts.
  if (head === "diagnosis" && arg) return void diagnosisPage(arg, who.profile.role);
  if (head === "photos") return void photosPage(who.profile.role);
  if (head === "parts") return void partsPage(who.profile.role);
  if (head === "certs") return void certsPage(who.profile.role);
  if (head === "contacts") return void contactsPage(who.profile.role);
  if (head === "me") return void mePage(who.profile.role, arg);
  if (head === "terminal") return void terminalPage(who.profile.role);
  if (head === "billing") return void billingPage(who.profile.role);
  if (head === "duplicates") return void duplicatesPage(who.profile.role);
  if (head === "shops") return void shopsPage(who.profile.role);
  if (head === "automations") return void automationsPage(who.profile.role);
  if (head === "desk") return void deskPage(who.profile.role);
  if (head === "queue") return void queuePage(who.profile.role);
  if (head === "command") return void commandPage(arg, hash.split("/")[3]);
  /* THE RENTALS SIDE. Served whichever side the switch is on, so a link to
     a trip from a text or a bookmark always opens the trip, but only for a
     shop that rents: anybody else typing the address gets their own home. */
  if (hasRentals(who.account.trade)) {
    if (head === "rentals") return void rentalsBoard();
    if (head === "trip" && arg) return void tripPage(arg);
    if (head === "newtrip") return void newTripPage(arg, hash.split("/")[3]);
    if (head === "rentcal") return void rentalCalendarPage();
    if (head === "rentmap") return void rentalMapPage();
  }
  /* THE ONE ROOM EVERY SHOP IS IN. No role gate: a forum that only
     admins can read is a mailing list. `new` before the id branch, or a
     topic called new would eat the compose screen. */
  if (head === "forum" && arg === "new") return void forumNewPage();
  if (head === "forum" && arg) return void forumTopicPage(arg);
  if (head === "forum") return void forumPage();
  // The old system had these as four separate menu items. Same four
  // words, one screen, so an old link still lands where it meant to.
  if (["pay", "payroll", "hours", "payouts", "mypay"].includes(head ?? "")) {
    const map: Record<string, string> = { payroll: "payroll", hours: "hours", payouts: "payouts", mypay: "mine" };
    return void payPage(who.profile.role, map[head ?? ""] ?? arg);
  }
  /* THE TIME CLOCK and the week behind it. See src/pages/clock.ts. */
  if (head === "clock") return void clockPage(who.profile.role);
  if (head === "timesheet") return void timesheetPage(who.profile.role);
  if (head === "workflow") return void workflowPage(currentSide());
  if (head === "walkthroughs") return void walkthroughsPage(who.profile.role);
  if (head === "more") return void morePage(who.profile.id, who.account.id);
  /* The owners' books. Two people only; see src/domain/books.ts. */
  if (head === "books") return void booksPage(who.profile.id);
  /* The Supercanic shop's live money. The same two people, and only while
     the desk is standing in Supercanic; see canSeeAccounting. */
  if (head === "accounting") return void accountingPage(who.profile.id, who.account.id);
  /* A route nobody serves. Off a desktop the day is the right landing and
     painting it here is the whole fix. In drawer mode it is not: the
     target is the panel, so the day would paint INSIDE it and leave two
     of them on screen. Going home is the same intent and it shuts the
     panel on the way. */
  if (drawer) { location.hash = "#/"; return; }
  return void home(who);
}

/**
 * THE HOME, FOR THE SIDE THIS BROWSER IS ON. Today at the yard on
 * Rentals, the day screen everywhere else. One function so the three
 * places that paint a home (no route, the day behind the drawer, and a
 * route nobody serves) cannot disagree about which one it is.
 */
function home(who: Extract<Who, { state: "in" }>): Promise<void> | void {
  /* A call desk's home is its Calls screen, behind a case opened beside it. */
  if (isCallDesk(who.account.id)) return callDeskPage();
  return currentSide() === "rentals" ? rentalsHome() : dashboardPage(who.profile.full_name);
}

/**
 * A PASSWORD LINK ARRIVING.
 *
 * Supabase hands the session back in the URL FRAGMENT, and this app uses
 * the fragment for routing — so `#access_token=...&type=recovery` looks
 * like a route, and the router would happily throw it away before the
 * Supabase client had a chance to read it.
 *
 * So the fragment is read FIRST, before anything routes, and the tokens
 * are handed to the client by hand rather than hoping detectSessionInUrl
 * wins the race. Then the fragment is cleared, because a live session
 * token sitting in the address bar is a session token that gets pasted
 * into a group chat.
 */
async function tookAPasswordLink(): Promise<boolean> {
  const raw = location.hash.startsWith("#") ? location.hash.slice(1) : location.hash;
  if (!/access_token=|error_description=/.test(raw)) return false;

  const p = new URLSearchParams(raw);
  const err = p.get("error_description");
  const access = p.get("access_token");
  const refresh = p.get("refresh_token");
  const kind = p.get("type");

  history.replaceState(null, "", location.pathname);

  if (err || !access || !refresh) {
    mount(`<div class="gate"><div class="card gate-card">
      <h1>That link did not work</h1>
      <p class="sub">${esc(err ?? "It may have already been used, or it has expired.")}
         Ask the shop to set your password instead — it takes them a moment.</p>
      <a class="btn primary wide" href="/index.html">Back to signing in</a>
    </div></div>`);
    return true;
  }

  const sb = await db();
  const { error } = await sb.auth.setSession({ access_token: access, refresh_token: refresh });
  if (error) {
    mount(`<div class="gate"><div class="card gate-card">
      <h1>That link did not work</h1><p class="sub">${esc(error.message)}</p>
      <a class="btn primary wide" href="/index.html">Back to signing in</a></div></div>`);
    return true;
  }

  // A recovery link is a person who came here to CHOOSE a password. Drop
  // them into the app without asking and they never set one, and the next
  // time they try to sign in they are stuck again.
  if (kind === "recovery") { renderNewPassword(); return true; }
  return false;
}

/** The only screen a recovery link should land on. */
function renderNewPassword(): void {
  const draw = (msg = "", busy = false) => {
    mount(`<div class="gate"><div class="card gate-card">
      <h1>Pick a password</h1>
      <p class="sub">You are signed in. Choose something you will remember —
         you will need it every day.</p>
      <label class="field"><span>New password</span>
        <input id="np" type="password" autocomplete="new-password"></label>
      <button class="btn primary wide" id="np-go" ${busy ? "disabled" : ""}>
        ${busy ? "Saving…" : "Save it and carry on"}</button>
      ${msg ? `<p class="msg bad">${esc(msg)}</p>` : ""}
    </div></div>`);

    const go = async () => {
      const v = (document.getElementById("np") as HTMLInputElement).value;
      if (v.length < 8) { draw("Use at least eight characters."); return; }
      draw("", true);
      try {
        const sb = await db();
        const { error } = await sb.auth.updateUser({ password: v });
        if (error) { draw(error.message); return; }
        location.hash = "#/";
        void start();
      } catch (e) { draw(e instanceof Error ? e.message : String(e)); }
    };
    document.getElementById("np-go")?.addEventListener("click", () => void go());
    document.getElementById("np")?.addEventListener("keydown", (e) => {
      if ((e as KeyboardEvent).key === "Enter") void go();
    });
  };
  draw();
}

/**
 * THE SCREEN THAT SHOWS WHEN NOTHING ELSE COULD.
 *
 * The shop rang on 3 September with "a black screen and we cannot log in",
 * and this is why. `start()` guarded exactly one line — loadConfig — and
 * everything after it ran naked: reading the stored session, looking up
 * the profile, drawing the first page. Any one of those throwing left the
 * promise rejected, `#app` empty, and an empty `#app` on a dark theme is a
 * black rectangle. No error, no sign-in form, nothing to press. The word
 * for that is not "a bug in the router", it is "the product vanished".
 *
 * A CRM is allowed to break. It is not allowed to break silently, in a
 * shop, with nobody able to tell whether it is the phone, the wifi or us.
 *
 * So: whatever happens, somebody sees a sentence and a way out. The way
 * out is deliberately the big one — clear what is stored on this device
 * and start again — because the single likeliest cause is a stored
 * session that cannot be read, and that is exactly what clearing fixes.
 */
function lastResort(why: string): void {
  mount(`<div class="gate"><div class="card gate-card">
    <h1>Something stopped</h1>
    <p class="sub">Shop Desk could not start on this device. Nothing you have
      done is lost &mdash; it is all on the server, not on the phone.</p>
    <p class="sub">Press the button. It clears what is saved here and takes you
      back to the sign in screen. You will need your password.</p>
    <button class="btn primary" id="lr-reset" style="width:100%;margin-top:6px">
      Clear this device and start again</button>
    <p class="fine" style="margin-top:14px">Still stuck? Ring the shop on
      (951) 904-2477. Tell them this said: <code>${
        String(why).slice(0, 160).replace(/[<>&]/g, "")}</code></p>
  </div></div>`);
  document.getElementById("lr-reset")?.addEventListener("click", () => {
    wipeDevice();
    location.replace("/index.html");
  });
}

/**
 * Everything this app has put on the device, gone. Wrapped individually
 * because a private window throws on the first one and the rest still
 * need doing.
 */
function wipeDevice(): void {
  try {
    // Only ours. Somebody may be signed into other things in this browser.
    for (const k of Object.keys(localStorage)) {
      if (/^(sb-|shopdesk|jobs:|theme$)/.test(k)) localStorage.removeItem(k);
    }
  } catch { /* private window */ }
  try { sessionStorage.clear(); } catch { /* private window */ }
  try {
    void caches?.keys().then((ks) => ks.forEach((k) => void caches.delete(k)));
  } catch { /* no cache api */ }
  try {
    void navigator.serviceWorker?.getRegistrations()
      .then((rs) => rs.forEach((r) => void r.unregister()));
  } catch { /* no worker */ }
}

/**
 * A NET UNDER THE WHOLE THING, not just under boot. A page that throws
 * while drawing leaves the app half painted or blank, and until now that
 * was silent too. If something escapes and there is nothing on screen,
 * say so.
 */
window.addEventListener("unhandledrejection", (e) => {
  const app = document.getElementById("app");
  if (app && app.innerHTML.trim().length < 40) {
    lastResort(String((e as PromiseRejectionEvent).reason ?? "unknown"));
  }
});
window.addEventListener("error", (e) => {
  const app = document.getElementById("app");
  if (app && app.innerHTML.trim().length < 40) {
    lastResort(String((e as ErrorEvent).message ?? "unknown"));
  }
});

/**
 * SIGNED IN TO THE WRONG DESK FOR THIS ADDRESS — hop to the right one.
 *
 * Anthony, 2026-10-17: "fix eber login issues to PPI". Eber is two logins
 * on this app: curiel288@ (Supercanic) and curiel288+ppi@ (the PPI desk).
 * Which desk opens follows the LOGIN, not the address bar, so typing his
 * everyday email and password on shop.prepurchaseauto.com opened the
 * Supercanic desk there, and the +ppi password was one he never kept --
 * he was resetting it every time (recovery mails 10-16 and 10-17).
 *
 * So when the login's desk lives on another host, and the same person has
 * a staff seat on THIS host's desk that /api/desks will hand over in one
 * tap (house staff only -- see server/routes/desks.ts), the page swaps to
 * that seat by itself. One sessionStorage mark per tab stops a loop if
 * the link ever lands back here on the wrong login.
 */
async function hopToThisHostsDesk(who: Extract<Who, { state: "in" }>): Promise<boolean> {
  const here = location.host.toLowerCase();
  if (!/^shop\.(supercanic|prepurchaseauto)\.com$/.test(here)) return false;
  const mine = (who.account.domain || "shop.supercanic.com").toLowerCase();
  const MARK = "desk-hop:" + here;
  /* Landed on the right desk: clear the mark so a later sign-in in this tab hops again. */
  if (mine === here) { try { sessionStorage.removeItem(MARK); } catch { /* fine */ } return false; }
  try { if (sessionStorage.getItem(MARK)) return false; sessionStorage.setItem(MARK, "1"); } catch { return false; }
  const d = (await myDesks()).find((x) => !x.current && x.oneTap && x.host.toLowerCase() === here);
  if (!d) return false;
  const url = await deskUrl(d);
  if (!/^https:\/\/[^/]+\/auth\/v1\/verify/.test(url)) return false;
  location.replace(url);
  return true;
}

export async function start(): Promise<void> {
  const bar = document.getElementById("bar");

  /**
   * A DOOR THAT ALWAYS OPENS. /index.html?reset=1 clears the device and
   * comes back clean, so a person on the phone to a stuck technician has
   * one link to give them rather than a walk through browser settings.
   */
  if (new URLSearchParams(location.search).get("reset") === "1") {
    wipeDevice();
    location.replace("/index.html");
    return;
  }

  try {
    await loadConfig();
  } catch {
    mount(`<div class="gate"><div class="card gate-card">
      <h1>Not quite up</h1>
      <p class="sub">The server did not answer <code>/api/config</code>. It may still be starting.</p>
    </div></div>`);
    return;
  }

  try {

  // Before anything routes: a password link has to be caught first.
  if (await tookAPasswordLink()) {
    if (bar) bar.hidden = true;
    document.getElementById("tabbar")?.remove();
    return;
  }

  const who = await whoAmI();

  /* The words every screen uses -- vehicle or door -- decided once, here,
     from the account this session already loaded. Before this line they are
     the auto words, which is what every shop had before migration 0107. */
  /* And the zone every screen reads a DAY in, from the same account, for the
     same reason: the schedule, the day map and the Desk tally all roll over
     at this shop's midnight rather than Supercanic's. Before this line it is
     Pacific, which is what every shop's screens used until 2026-09-18. */
  if (who.state === "in") {
    primeWords(who.account.trade, who.account.name);
    primeShopDomain(who.account.domain);
    primeZone(who.account.timezone);
  }

  if (who.state !== "in") {
    if (bar) bar.hidden = true;
    document.getElementById("tabbar")?.remove();
    if (who.state === "signed-out") { renderSignIn(() => void start()); return; }
    if (who.state === "no-profile") { renderNoProfile(who, () => void start()); return; }
    return;
  }

    if (await hopToThisHostsDesk(who)) return;

    await route(who);

    /* Ask the phone for a push token, if this is a phone. After route() and
       deliberately not awaited: registration can sit for seconds waiting on
       a gateway, and nobody opening a job should wait behind it. Does
       nothing at all in a browser, and nothing today on a phone either --
       there is no FCM project and no APNs key yet, so no token arrives. See
       src/data/push.ts. */
    void registerThisDevice(who.account.id, who.profile.id);

    // Re-route on navigation, but re-check identity only at boot and at
    // sign-out. Asking the server who you are on every click is a round
    // trip that answers the same thing every time.
    //
    // Guarded, because a page that throws while drawing used to blank the
    // whole app and leave nothing behind to explain it.
    const again = () => {
      void (async () => {
        try { await route(who); }
        catch (e) { lastResort(e instanceof Error ? e.message : String(e)); }
      })();
    };
    window.onhashchange = again;
    /* A window dragged across the line is a navigation as far as the
       layout is concerned: the same hash has to be drawn a different way.
       Re-running the route is how, rather than a second renderer that
       moves the panel and drifts from this one. */
    onDrawerModeChange(again);
  } catch (e) {
    lastResort(e instanceof Error ? e.message : String(e));
  }
}

void start();
